import {
  Box,
  Typography,
  LinearProgress,
  Grid,
  Paper,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

function UsageImpactPreview({ impact }) {
  if (!impact) return null;

  const formatNumber = (num) => num.toLocaleString();

  function UsageProgressBar({ current, limit, label, showWarning }) {
    const percentage = Math.min((current / limit) * 100, 100);
    const isOverLimit = current > limit;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {showWarning && isOverLimit && (
            <Tooltip title="Usage exceeds new tier limit">
              <WarningIcon color="warning" fontSize="small" />
            </Tooltip>
          )}
          {showWarning && !isOverLimit && (
            <Tooltip title="Usage within new tier limit">
              <CheckIcon color="success" fontSize="small" />
            </Tooltip>
          )}
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              color={isOverLimit ? "warning" : "primary"}
              sx={{
                height: 8,
                borderRadius: 1
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
            {formatNumber(current)} / {formatNumber(limit)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Usage Impact Analysis
        <Tooltip title="Shows how your current usage patterns align with the new tier limits">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Month Usage
            </Typography>
            
            {/* Monthly API Calls */}
            <UsageProgressBar
              current={impact.usage.current}
              limit={impact.usage.newLimit}
              label="API Calls"
              showWarning
            />

            {/* API Keys */}
            <UsageProgressBar
              current={impact.apiKeyCount.current}
              limit={impact.apiKeyCount.allowed}
              label="API Keys"
              showWarning
            />

            {/* Operation Breakdown */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Operation Type Breakdown
            </Typography>
            
            {impact.usage.operations && Object.entries(impact.usage.operations).map(([type, count]) => (
              <UsageProgressBar
                key={type}
                current={count}
                limit={impact.usage.newLimit}
                label={type.charAt(0).toUpperCase() + type.slice(1)}
              />
            ))}

            {/* Usage Warnings */}
            {impact.usage.overageRisk && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  ⚠️ Your current usage pattern may lead to overages under the new tier limits.
                  Consider monitoring your usage more closely or choosing a higher tier.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UsageImpactPreview; 